import {
  Headphones,
  HeadsetMicOutlined,
  KeyboardArrowDown,
  PhoneOutlined,
  PowerSettingsNew,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';

import { AGENT_STATUS_COLORS, AGENT_STATUS_DISPLAY } from '../../../../constants/agentStatus';
import { CallStatus } from '../../../../contexts/PhoneCallsContext/phone-calls.types';
import { usePhoneCalls } from '../../../../contexts/PhoneCallsContext/PhoneCallsContext';
import { AgentAvailabilityStatus, CallDirection } from '../../../../interfaces/calls';
import { getCapitalizedWord } from '../../../../util/stringFormat';

interface MediaDevice {
  deviceId: string;
  label: string;
}

export const AgentAvailabilitySelector: FC = () => {
  const {
    agent,
    agentAvailability,
    activeCallState,
    isDeviceOn,
    openIncomingCallModal,
    openOutgoingCallModal,
    updateAgentAvailability,
    toggleDevice,
  } = usePhoneCalls();

  const [micMenuAnchor, setMicMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedMicDevice, setSelectedMicDevice] = useState<MediaDevice | null>(null);
  const [micDevices, setMicDevices] = useState<MediaDevice[]>([]);
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [deviceIsTurningOn, setDeviceIsTurningOn] = useState<boolean>(false);

  const handleActiveCallClick = () => {
    if (activeCallState.call && activeCallState.direction === CallDirection.INBOUND) {
      openIncomingCallModal();
    } else if (activeCallState.call && activeCallState.direction === CallDirection.OUTBOUND) {
      openOutgoingCallModal();
    } else {
      openOutgoingCallModal();
    }
  };

  const getButtonColor = () => {
    switch (activeCallState.status) {
      case CallStatus.OnHold:
      case CallStatus.Muted:
      case CallStatus.Transferring:
        return '#FF9800';
      case CallStatus.Reconnecting:
        return '#F44336';
      case CallStatus.Failed:
      case CallStatus.Rejected:
        return '#e42020';
      case CallStatus.Idle:
      case CallStatus.Ringing:
      case CallStatus.Calling:
      case CallStatus.OnCall:
      case CallStatus.Ending:
      case CallStatus.Ended:
      case CallStatus.Reconnected:
      default:
        return '#4CAF50';
    }
  };

  const handleMicMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMicMenuAnchor(event.currentTarget);
  };

  const handleMicMenuClose = () => {
    setMicMenuAnchor(null);
  };

  const handleMicDeviceSelect = (device: MediaDevice) => {
    setSelectedMicDevice(device);
    handleMicMenuClose();
  };

  const getMediaDevices = async () => {
    try {
      // Request microphone permission first
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setHasPermission(true);

      // Stop the stream immediately since we only needed permission
      stream.getTracks().forEach((track) => track.stop());

      // Get the list of audio input devices
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputDevices = devices
        .filter((device) => device.kind === 'audioinput')
        .map((device) => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId.slice(0, 8)}`,
        }));

      setMicDevices(audioInputDevices);

      // Set default device if none selected
      if (!selectedMicDevice && audioInputDevices.length > 0) {
        const defaultDevice =
          audioInputDevices.find(
            (device) =>
              device.deviceId === 'default' || device.label.toLowerCase().includes('default'),
          ) || audioInputDevices[0];
        setSelectedMicDevice(defaultDevice);
      }
    } catch (error) {
      console.error('Error accessing media devices:', error);
      setHasPermission(false);
      const fallbackDevice = { deviceId: 'default', label: 'Default Microphone' };
      setMicDevices([fallbackDevice]);
      setSelectedMicDevice(fallbackDevice);
    }
  };

  useEffect(() => {
    if (navigator.mediaDevices && typeof navigator.mediaDevices.enumerateDevices === 'function') {
      getMediaDevices();

      // Listen for device changes
      const handleDeviceChange = () => {
        getMediaDevices();
      };

      navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

      return () => {
        navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
      };
    } else {
      const fallbackDevice = { deviceId: 'default', label: 'Default Microphone' };
      setMicDevices([fallbackDevice]);
      setSelectedMicDevice(fallbackDevice);
    }
  }, []);

  const getDeviceButtonColor = () => {
    if (deviceIsTurningOn) {
      return '#FF9800';
    }

    if (!isDeviceOn) {
      return '#4CAF50';
    }

    return '#e42020';
  };

  const getDeviceTooltip = () => {
    if (!isDeviceOn) {
      return 'Turn on device';
    }

    return 'Turn off device';
  };

  return (
    <Box
      display='flex'
      alignItems='center'
      sx={{
        backgroundColor: '#FFFFFF',
        borderRadius: '25px',
        padding: '0 6px',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        gap: 2,
        minWidth: '350px',
      }}>
      <Box display='flex' alignItems='center' minWidth='100px' gap={1}>
        <Tooltip title={getDeviceTooltip()}>
          <IconButton
            size='small'
            onClick={async () => {
              setDeviceIsTurningOn(true);
              await toggleDevice();
              setDeviceIsTurningOn(false);
            }}
            disabled={!!activeCallState.call}
            sx={{
              border: `1px solid ${getDeviceButtonColor()}`,
              borderRadius: '15px',
              padding: '4px',
              color: getDeviceButtonColor(),
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
              '&:disabled': {
                border: '1px solid rgba(0, 0, 0, 0.26)',
                color: 'rgba(0, 0, 0, 0.26)',
              },
            }}>
            {deviceIsTurningOn ? (
              <CircularProgress size={17} sx={{ color: getDeviceButtonColor() }} />
            ) : (
              <PowerSettingsNew sx={{ fontSize: '17px', color: getDeviceButtonColor() }} />
            )}
          </IconButton>
        </Tooltip>

        {/* Headphones Icon with Dropdown for Mic Selection */}
        <Box display='flex' alignItems='center'>
          <IconButton
            size='small'
            onClick={handleMicMenuOpen}
            disabled={!isDeviceOn}
            sx={{
              border: '1px solid #C4C4C4',
              borderRadius: '15px',
              padding: '4px',
              gap: '5px',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
              '&:disabled': {
                border: '1px solid rgba(0, 0, 0, 0.26)',
                color: isDeviceOn ? '#404040' : '#cdcdcd',
              },
            }}>
            <HeadsetMicOutlined
              sx={{
                fontSize: '17px',
                paddingLeft: '4px',
                paddingRight: '4px',
                color: isDeviceOn ? '#404040' : '#cdcdcd',
              }}
            />
            <KeyboardArrowDown
              sx={{
                fontSize: '16px',
                paddingLeft: '4px',
                paddingRight: '4px',
                borderLeft: '1px solid #C4C4C4',
                color: isDeviceOn ? '#404040' : '#cdcdcd',
              }}
            />
          </IconButton>

          <Menu
            anchorEl={micMenuAnchor}
            open={Boolean(micMenuAnchor)}
            onClose={handleMicMenuClose}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: '200px',
              },
            }}>
            {!hasPermission && (
              <MenuItem disabled>
                <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                  Microphone permission required
                </Typography>
              </MenuItem>
            )}
            {micDevices.map((device) => (
              <MenuItem
                key={device.deviceId}
                onClick={() => handleMicDeviceSelect(device)}
                selected={selectedMicDevice?.deviceId === device.deviceId}>
                <Headphones sx={{ mr: 1, fontSize: '18px' }} />
                <Typography sx={{ fontSize: '14px' }}>{device.label}</Typography>
              </MenuItem>
            ))}
            {hasPermission && micDevices.length === 0 && (
              <MenuItem disabled>
                <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                  No microphones found
                </Typography>
              </MenuItem>
            )}
          </Menu>
        </Box>

        {/* Phone Icon with Call Status */}
        <Button
          id='active-call'
          size='small'
          disabled={
            !isDeviceOn ||
            agentAvailability?.currentStatus === AgentAvailabilityStatus.OFFLINE ||
            agentAvailability?.currentStatus === AgentAvailabilityStatus.DISABLED
          }
          onClick={handleActiveCallClick}
          variant='outlined'
          startIcon={
            <PhoneOutlined
              sx={{
                fontSize: '19px',
              }}
            />
          }
          sx={{
            border: `1px solid ${getButtonColor()}`,
            borderRadius: '10px',
            color: getButtonColor(),
            padding: '2px 9px',
            cursor: 'pointer',
            textTransform: 'capitalize',
          }}>
          {activeCallState.status === CallStatus.Idle
            ? 'Start'
            : getCapitalizedWord(activeCallState.status)}
        </Button>
      </Box>

      {/* Status Display */}
      <Box display='flex' alignItems='center' flex={1} justifyContent='center' gap={1} mx={1}>
        <Typography
          sx={{
            color: isDeviceOn ? '#404040' : '#cdcdcd',
            fontSize: '14px',
            fontWeight: 500,
          }}
          title={
            agent?.trackingNumbers?.incoming?.length
              ? agent?.trackingNumbers.incoming.map((number) => number.number).join(', ')
              : ''
          }>
          {agent
            ? agent?.trackingNumbers?.incoming?.length
              ? agent?.trackingNumbers.incoming[0].number || '-'
              : 'Unassigned'
            : '-'}
        </Typography>
      </Box>

      {/* Status Selector */}
      <FormControl size='small' sx={{ minWidth: '150px' }}>
        <Select
          disabled={!isDeviceOn || (!!agent && !agent?.phoneNumber) || !!activeCallState.call}
          value={agentAvailability.currentStatus}
          sx={{
            backgroundColor: 'transparent',
            borderRadius: '15px',
            fontSize: '13px',
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .MuiSelect-icon': {
              color: '#666',
            },
          }}
          onChange={async (e) => {
            const newStatus = e.target.value as AgentAvailabilityStatus;
            await updateAgentAvailability({ status: newStatus });
          }}
          displayEmpty
          renderValue={(selected) => (
            <Box display='flex' alignItems='center' gap={1}>
              <Box
                sx={{
                  width: '8px',
                  height: '8px',
                  backgroundColor: AGENT_STATUS_COLORS[selected],
                  borderRadius: '50%',
                }}
              />
              <Typography
                sx={{
                  textTransform: 'capitalize',
                  margin: 0,
                  color: '#000000DE',
                  fontSize: '13px',
                }}>
                {selected.split('_').join(' ').toLowerCase()}
              </Typography>
            </Box>
          )}>
          {[
            AgentAvailabilityStatus.AVAILABLE,
            AgentAvailabilityStatus.AWAY,
            AgentAvailabilityStatus.DO_NOT_DISTURB,
            AgentAvailabilityStatus.OFFLINE,
            AgentAvailabilityStatus.ON_CALL,
            AgentAvailabilityStatus.DISABLED,
          ].map((status) => (
            <MenuItem
              key={status}
              value={status}
              sx={{
                display:
                  status === AgentAvailabilityStatus.AVAILABLE ||
                  status === AgentAvailabilityStatus.DO_NOT_DISTURB
                    ? 'flex'
                    : 'none',
              }}>
              <Box display='flex' alignItems='center' gap={1}>
                <Box
                  sx={{
                    width: '8px',
                    height: '8px',
                    backgroundColor: AGENT_STATUS_COLORS[status],
                    borderRadius: '50%',
                  }}
                />
                <Typography sx={{ textTransform: 'capitalize', fontSize: '13px' }}>
                  {AGENT_STATUS_DISPLAY[status]}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};
